import { ChangeEventHandler, FC, forwardRef, useEffect, useState } from 'react';

import { InputProps } from './Input.types';
import {
  CautionIconStyled,
  ErrorBoxStyled,
  ErrorDescriptionStyled,
  InputContainerStyled,
  InputStyled,
  StyledInputLabel,
  StyledLabel,
} from './Input.styles';
import { inputWidth } from '@common/utils';
import { Variants } from '@common/types';

const Input: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    autoComplete = 'off',
    className,
    css,
    defaultValue,
    disabled,
    // This prop is mainly for the InputDateSelector
    direction = 'vertical',
    error,
    errorDescription,
    form,
    fullWidth,
    id,
    label,
    width,
    name,
    onBlur,
    onChange,
    onKeyDown,
    placeholder,
    role,
    readOnly,
    testId = 'input-test-id',
    type = 'text',
    value,
    variant = Variants.PRIMARY,
    secondaryLabel,
    isSensitive = false, // New prop to indicate if the input is sensitive
    ...rest
  } = props;
  const inputProps = {
    autoComplete,
    error,
    form,
    id,
    label,
    name,
    onBlur,
    placeholder,
    readOnly,
    role,
    type,
    disabled,
  };

  const hasDefaultValue = typeof defaultValue !== 'undefined';

  const [inputValue, setInputValue] = useState<string | number | undefined>(hasDefaultValue ? defaultValue : '');

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event): void => {
    setInputValue(event.target.value);
    onChange?.(event);
  };

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  return (
    <InputContainerStyled css={{ width: inputWidth(width, fullWidth), ...css }} className={className}>
      <StyledInputLabel
        error={!!error}
        variant={variant}
        css={
          direction === 'horizontal' && variant === Variants.PRIMARY
            ? {
                display: 'flex',
                flexDirection: 'row',
                borderBottom: `$borderWidths$1 solid ${error ? '$error' : '$darkGray700'}`,
                gap: '$1',
                '&:hover': {
                  borderBottom: '$borderWidths$1 solid $charcoal300',
                },
                '&:focus': {
                  borderBottom: '$borderWidths$2 solid $darkBlue300',
                },
              }
            : undefined
        }
      >
        <StyledLabel
          variant={variant}
          htmlFor={id}
          form={form}
          data-testid={`${testId}-label`}
          css={direction === 'horizontal' ? { marginBottom: '-$1' } : undefined}
        >
          {label}
        </StyledLabel>
        {secondaryLabel && variant === Variants.PRIMARY && (
          <StyledLabel htmlFor={id} css={{ fontWeight: 300, fontSize: '14px', marginLeft: '$1' }}>
            <em>{secondaryLabel}</em>
          </StyledLabel>
        )}
        <InputStyled
          {...inputProps}
          {...rest}
          variant={variant}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          ref={ref}
          data-testid={testId}
          css={direction === 'horizontal' && Variants.PRIMARY ? { marginBottom: '-1px' } : undefined}
          className={isSensitive ? 'rb-mask' : ''} // Add class for sensitive inputs
        />
      </StyledInputLabel>
      {error != null && error && (
        <div>
          <ErrorBoxStyled>
            <CautionIconStyled />
            <ErrorDescriptionStyled data-testid={`${testId}-error`}>{errorDescription}</ErrorDescriptionStyled>
          </ErrorBoxStyled>
        </div>
      )}
    </InputContainerStyled>
  );
});

Input.displayName = 'Input';

export default Input;
